// src/api/telephonyApi.ts

import { baseApi } from './baseApi';
import type {
  TwilioPhoneNumber,
  RegisterPhoneNumberRequest,
  UpdatePhoneNumberRequest,
  AssignPhoneNumberRequest,
  SyncPhoneNumbersResponse,
  CallSession,
  Voicemail,
  PaginatedResponse,
  CallSessionFilters,
  VoicemailFilters,
} from '../types/api';

/**
 * Phase 3.5 — workspace voice configuration. Mirrors the
 * `EffectiveVoiceConfig` shape returned by the server's
 * `/api/telephony/voice/config` endpoint.
 */
export interface VoiceConfig {
  recordingMode: 'off' | 'always';
  /** Provider-Owned Recording — the voice integration's Manual/Automatic default. */
  voipRecordingMode: 'manual' | 'automatic';
  playRecordingDisclosure: boolean;
  /**
   * The exact disclosure text the server will play before <Dial> when
   * both recordingMode='always' and playRecordingDisclosure=true. Read-
   * only — Phase 3.5 uses a fixed string to keep operators from typing
   * legally ambiguous wording.
   */
  disclosureText: string;
  /**
   * Phase 1 §6.2 — who owns the underlying IntegrationConnection row.
   *   'outbound' → mirror of an Outbound WorkspaceProvider; per-provider
   *                recording mode is canonical on Outbound and edited
   *                from Settings → Providers (per-provider kebab). The
   *                workspace-level Manual/Automatic radios on this
   *                card are hidden for these workspaces.
   *   'crm'      → native CRM row; workspace-level radios still render.
   * Older backends without this field are treated as 'crm' (safe
   * default — CRM-native behavior).
   */
  managedBy?: 'crm' | 'outbound';
}

// ── Recordings tab (Phase 3 UNION list) ─────────────────────────
export interface RecordingListRow {
  id: string;
  source: 'segment' | 'legacy';
  workspaceId: string;
  callSessionId: string | null;
  twilioCallSid: string;
  twilioRecordingSid: string | null;
  recordingUrl: string | null;
  duration: number | null;
  direction: 'inbound' | 'outbound';
  fromNumber: string | null;
  toNumber: string | null;
  createdAt: string;
  contact: { id: string; fullName: string | null; phone: string | null } | null;
  assignedUser: { id: string; fullName: string | null } | null;
  twilioPhoneNumber:
    | { id: string; phoneNumber: string; label: string | null }
    | null;
  /** Server-supplied playback URL — either /call-sessions/:id/recording
   *  (legacy) or /call-recordings/:id/recording (new segment). The UI
   *  streams from this directly; no branching needed at render time. */
  playbackUrl: string;
  status: string;
  /**
   * CRM S3 ingestion state. Drives the row badge:
   *   completed → Available (bytes served from CRM S3)
   *   pending | downloading | uploading → Processing
   *   failed → Failed (playback still works via Twilio proxy)
   *   not_started → Available (legacy rows / flag disabled — Twilio proxy)
   */
  ingestionStatus:
    | 'not_started'
    | 'pending'
    | 'downloading'
    | 'uploading'
    | 'completed'
    | 'failed';
  /** true when bytes are served from CRM S3 (storageProvider='s3'). */
  storedInCrmS3: boolean;
  /**
   * Server-derived UI state for the CRM Recordings tab. Drives the
   * status badge + Play button enablement WITHOUT relying on the
   * caller re-deriving from `status` + `recordingUrl` (which was the
   * pre-relaxation behaviour and hid rows until the completion
   * callback landed).
   *
   *   ready       → recordingUrl present + status='completed' → play enabled
   *   processing  → recording started but the completion callback has not
   *                 landed yet AND still within the server's grace window
   *                 → play disabled, "Processing…" badge
   *   unavailable → grace window expired without a url, or status='failed'
   *                 → play disabled, "Unavailable" badge
   *
   * Optional to tolerate older CRM backend deploys that don't yet emit
   * the field; callers must fall back to the legacy derivation from
   * `recordingUrl + status` when this is absent.
   */
  processingState?: 'ready' | 'processing' | 'unavailable';
}

export interface RecordingListFilters {
  q?: string;
  direction?: 'inbound' | 'outbound';
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'createdAt' | 'duration';
  sortDir?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
}

export const telephonyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ============================================================
    // List Phone Numbers
    // ============================================================
    listPhoneNumbers: builder.query<TwilioPhoneNumber[], void>({
      query: () => '/telephony/phone-numbers',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'TwilioPhoneNumber' as const, id })),
              { type: 'TwilioPhoneNumber', id: 'LIST' },
            ]
          : [{ type: 'TwilioPhoneNumber', id: 'LIST' }],
    }),

    // ============================================================
    // Get Single Phone Number
    // ============================================================
    getPhoneNumber: builder.query<TwilioPhoneNumber, string>({
      query: (id) => `/telephony/phone-numbers/${id}`,
      providesTags: (result, error, id) => [{ type: 'TwilioPhoneNumber', id }],
    }),

    // ============================================================
    // Register Phone Number
    // ============================================================
    registerPhoneNumber: builder.mutation<TwilioPhoneNumber, RegisterPhoneNumberRequest>({
      query: (body) => ({
        url: '/telephony/phone-numbers',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'TwilioPhoneNumber', id: 'LIST' }],
    }),

    // ============================================================
    // Update Phone Number
    // ============================================================
    updatePhoneNumber: builder.mutation<TwilioPhoneNumber, { id: string; data: UpdatePhoneNumberRequest }>({
      query: ({ id, data }) => ({
        url: `/telephony/phone-numbers/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'TwilioPhoneNumber', id },
        { type: 'TwilioPhoneNumber', id: 'LIST' },
      ],
    }),

    // ============================================================
    // Assign Phone Number
    // ============================================================
    assignPhoneNumber: builder.mutation<TwilioPhoneNumber, { id: string; data: AssignPhoneNumberRequest }>({
      query: ({ id, data }) => ({
        url: `/telephony/phone-numbers/${id}/assign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'TwilioPhoneNumber', id },
        { type: 'TwilioPhoneNumber', id: 'LIST' },
      ],
    }),

    // ============================================================
    // Delete Phone Number
    // ============================================================
    deletePhoneNumber: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/telephony/phone-numbers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'TwilioPhoneNumber', id: 'LIST' }],
    }),

    // ============================================================
    // Sync Phone Numbers from Twilio
    // ============================================================
    syncPhoneNumbers: builder.mutation<SyncPhoneNumbersResponse, void>({
      query: () => ({
        url: '/telephony/sync',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'TwilioPhoneNumber', id: 'LIST' }],
    }),

    // ============================================================
    // List Phone Numbers with Assignments (enriched)
    // ============================================================
    listPhoneNumbersWithAssignments: builder.query<TwilioPhoneNumber[], void>({
      query: () => '/telephony/phone-numbers/with-assignments',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'TwilioPhoneNumber' as const, id })),
              { type: 'TwilioPhoneNumber', id: 'LIST' },
            ]
          : [{ type: 'TwilioPhoneNumber', id: 'LIST' }],
    }),

    // ============================================================
    // Set Phone Number as Primary
    // ============================================================
    setPhoneNumberPrimary: builder.mutation<TwilioPhoneNumber, string>({
      query: (id) => ({
        url: `/telephony/phone-numbers/${id}/set-primary`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'TwilioPhoneNumber', id: 'LIST' }],
    }),

    // ============================================================
    // Update Phone Number Label
    // ============================================================
    updatePhoneNumberLabel: builder.mutation<TwilioPhoneNumber, { id: string; label: string }>({
      query: ({ id, label }) => ({
        url: `/telephony/phone-numbers/${id}/label`,
        method: 'PUT',
        body: { label },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'TwilioPhoneNumber', id },
        { type: 'TwilioPhoneNumber', id: 'LIST' },
      ],
    }),

    // ============================================================
    // Get Default Outbound Number
    // ============================================================
    getDefaultOutboundNumber: builder.query<TwilioPhoneNumber, void>({
      query: () => '/telephony/default-outbound-number',
      providesTags: ['DefaultOutboundNumber'],
    }),

    // ============================================================
    // List Call Sessions
    // ============================================================
    listCallSessions: builder.query<PaginatedResponse<CallSession>, CallSessionFilters | void>({
      query: (filters) => ({
        url: '/telephony/call-sessions',
        params: filters || {},
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: 'CallSession' as const, id })),
              { type: 'CallSession', id: 'LIST' },
            ]
          : [{ type: 'CallSession', id: 'LIST' }],
    }),

    // ============================================================
    // Get Single Call Session
    // ============================================================
    getCallSession: builder.query<CallSession, string>({
      query: (id) => `/telephony/call-sessions/${id}`,
      providesTags: (result, error, id) => [{ type: 'CallSession', id }],
    }),

    // ============================================================
    // Mid-call recording — start / stop / list (Phase 2 + 3)
    // ============================================================
    startCallRecording: builder.mutation<
      // Response shape varies by branch:
      //   • Success (201) / idempotency hit (200): `recording` is present.
      //   • TwiML-owns-automatic early return (200): NO `recording` field,
      //     server sets `ownedBy: 'twiml'` — recording is active but was
      //     started by the initial TwiML, not by this mid-call endpoint,
      //     so no CRM segment row exists yet (see
      //     server/src/controllers/callRecordingController.ts:233-250).
      //   Callers MUST guard on `res.recording` before dereferencing it.
      {
        ok: true;
        recording?: {
          id: string;
          twilioCallSid?: string;
          twilioRecordingSid: string | null;
          status: string;
          startedAt?: string;
        };
        idempotent?: boolean;
        recordingStarted?: boolean;
        ownedBy?: 'twiml';
      },
      { callSid: string; outboundCallId?: string }
    >({
      query: ({ callSid, outboundCallId }) => ({
        url: `/telephony/calls/${encodeURIComponent(callSid)}/recording/start`,
        method: 'POST',
        // Phase 1 linking — for Outbound-managed CRM calls the CallSession
        // placeholder is created at prepare time with `twilioCallSid: null`
        // (Twilio's SID is only stamped by the recording-complete webhook).
        // Send the `outboundCallId` we got from the prepare response so the
        // backend can find the CallSession via that chain and link the new
        // CallRecording.callSessionId immediately — otherwise the Recordings
        // tab shows "Unknown caller" + em-dashes for direction / duration /
        // agent / phone number until the (currently-blocked) callback lands.
        // Optional; native CRM calls skip it and behave byte-identically.
        body:
          typeof outboundCallId === 'string' && outboundCallId.length > 0
            ? { outboundCallId }
            : {},
      }),
      invalidatesTags: [{ type: 'CallSession', id: 'LIST' }],
    }),

    // ============================================================
    // Recording Mode preference + effective-state
    // ============================================================
    getRecordingMode: builder.query<
      { mode: 'automatic' | 'manual'; source: 'user' | 'default' },
      void
    >({
      query: () => ({ url: '/telephony/recording-mode', method: 'GET' }),
    }),
    updateRecordingMode: builder.mutation<
      { mode: 'automatic' | 'manual'; source: 'user' },
      { mode: 'automatic' | 'manual' }
    >({
      query: (body) => ({
        url: '/telephony/recording-mode',
        method: 'PUT',
        body,
      }),
    }),
    getRecordingEffectiveState: builder.query<
      {
        ok: true;
        mode: 'automatic' | 'manual' | 'never';
        locked: boolean;
        reason:
          | 'compliance_always'
          | 'compliance_never'
          | 'campaign_override'
          | 'call_override'
          | 'user_preference'
          | 'default';
        canStart: boolean;
        canStop: boolean;
        status:
          | 'idle'
          | 'starting'
          | 'recording'
          | 'stopping'
          | 'completed'
          | 'failed';
        recordingSid: string | null;
        recordingUrl: string | null;
        startedAt: string | null;
        stoppedAt: string | null;
      },
      { callSid: string }
    >({
      query: ({ callSid }) => ({
        url: `/telephony/calls/${encodeURIComponent(callSid)}/recording/effective-state`,
        method: 'GET',
      }),
    }),
    stopCallRecording: builder.mutation<
      { ok: true; recording: { id: string; status: string }; idempotent?: boolean },
      { recordingId: string }
    >({
      query: ({ recordingId }) => ({
        url: `/telephony/recordings/${encodeURIComponent(recordingId)}/stop`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'CallSession', id: 'LIST' }],
    }),
    // Recordings tab data source (Phase 3): UNION of new call_recordings
    // segments and legacy CallSession recordings.
    listRecordings: builder.query<
      {
        items: RecordingListRow[];
        total: number;
        limit: number;
        offset: number;
      },
      RecordingListFilters | void
    >({
      query: (filters) => ({
        url: '/telephony/recordings',
        params: filters || {},
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({
                type: 'CallSession' as const,
                id,
              })),
              { type: 'CallSession', id: 'LIST' },
            ]
          : [{ type: 'CallSession', id: 'LIST' }],
    }),

    // ============================================================
    // List Voicemails
    // ============================================================
    listVoicemails: builder.query<PaginatedResponse<Voicemail>, VoicemailFilters | void>({
      query: (filters) => ({
        url: '/telephony/voicemails',
        params: filters || {},
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: 'Voicemail' as const, id })),
              { type: 'Voicemail', id: 'LIST' },
            ]
          : [{ type: 'Voicemail', id: 'LIST' }],
    }),

    // ============================================================
    // Get Single Voicemail
    // ============================================================
    getVoicemail: builder.query<Voicemail, string>({
      query: (id) => `/telephony/voicemails/${id}`,
      providesTags: (result, error, id) => [{ type: 'Voicemail', id }],
    }),

    // ============================================================
    // Mark Voicemail as Listened
    // ============================================================
    markVoicemailListened: builder.mutation<Voicemail, string>({
      query: (id) => ({
        url: `/telephony/voicemails/${id}/listened`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Voicemail', id },
        { type: 'Voicemail', id: 'LIST' },
      ],
    }),

    // ============================================================
    // Archive Voicemail
    // ============================================================
    archiveVoicemail: builder.mutation<Voicemail, string>({
      query: (id) => ({
        url: `/telephony/voicemails/${id}/archive`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Voicemail', id },
        { type: 'Voicemail', id: 'LIST' },
      ],
    }),

    // ============================================================
    // Initiate Outbound Call
    // ============================================================
    initiateCall: builder.mutation<
      {
        success: boolean;
        callSid: string;
        callSessionId: string;
        from: string;
        to: string;
      },
      { toNumber: string; fromNumberId?: string; contactId?: string }
    >({
      query: (body) => ({
        url: '/telephony/calls/initiate',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'CallSession', id: 'LIST' }],
    }),

    // ============================================================
    // Browser VoIP Voice Access Token (Phase 2)
    // ============================================================
    // Mints a Twilio Voice access token signed with the workspace's
    // API Key + Secret. Used by the @twilio/voice-sdk Device.
    //
    // Server endpoint: POST /api/telephony/voice/token
    // Auth stack: requireHybridAuth + telephony.manage + phone.call.
    //
    // Failure modes (mapped in useTwilioVoiceDevice):
    //   401 — auth expired
    //   403 CAPABILITY_DENIED — phone.call missing
    //   503 TWILIO_NOT_CONFIGURED — workspace integration missing
    //   503 TWILIO_VOIP_NOT_CONFIGURED — VoIP-specific fields missing
    fetchVoiceToken: builder.mutation<
      {
        token: string;
        identity: string;
        expiresAt: string;
        ttlSeconds: number;
        twimlAppSid: string;
        // Phase 3 — workspace's voice recording mode. Drives the
        // "Recording" badge on the active call shell so the UI matches
        // server-side reality.
        recordingMode: 'off' | 'always';
        // Per-provider browser calling — echo of the exact Outbound
        // `WorkspaceProvider.id` this token was minted against. Present
        // whenever the caller sent `outboundProviderId` in the request
        // body OR when Outbound resolved the mobile-mirror single voice
        // channel. Absent for workspace-default resolution. Consumers
        // that ASKED for a specific provider MUST verify this equals
        // what they sent — a mismatch means the JWT is bound to the
        // wrong Twilio account and Device.connect must be refused.
        outboundProviderId?: string;
        // Phase 3.5 — full effective config (recordingMode + disclosure
        // flag + the exact disclosure text the server will play). The
        // active-call shell uses these to render an honest indicator.
        effectiveVoiceConfig: VoiceConfig;
        /**
         * Inbound calling (ADDITIVE, Phase IV).
         *
         * `incomingAllow` is the ONLY authority the browser obeys: it reflects
         * what the server actually put in the Twilio VoiceGrant. A client that
         * ignored it and registered incoming handlers anyway would simply never
         * be offered a call, because the grant is what Twilio enforces.
         *
         * `incomingEnabled` is the feature flag itself, reported separately so
         * the UI can distinguish "the feature is off" from "the feature is on
         * but this user/workspace is not eligible".
         *
         * Optional so an older server (which sends neither) is read as false.
         */
        incomingAllow?: boolean;
        incomingEnabled?: boolean;
        /**
         * Option B — set to 'outbound' when the CRM voice-token endpoint
         * proxied the mint to Outbound because the Twilio row for this
         * workspace is an Outbound-managed Provider-Sync mirror. Set to
         * 'crm' (or absent) when the mint happened locally against a
         * native CRM Twilio row. Drives the "Managed by Outbound"
         * caption on the ready state of the readiness card.
         */
        managedBy?: 'crm' | 'outbound';
      },
      // Per-provider browser calling. Optional; when omitted the mutation
      // preserves legacy default-provider behaviour byte-identically for
      // every existing caller. When supplied it is forwarded to the CRM
      // controller and thence to Outbound's token minter, which validates
      // and mints against that exact WorkspaceProvider.
      { outboundProviderId?: string } | void
    >({
      query: (arg) => ({
        url: '/telephony/voice/token',
        method: 'POST',
        body:
          arg && typeof arg === 'object' && typeof arg.outboundProviderId === 'string'
            ? { outboundProviderId: arg.outboundProviderId }
            : {},
      }),
    }),

    // ============================================================
    // Browser VoIP Voice Configuration (Phase 3.5)
    // ============================================================
    // Read-only effective config + admin update. Backed by
    // `IntegrationConnection.settings.voiceRecordingMode` and
    // `.playRecordingDisclosure`. Read requires phone.read; write
    // requires phone.call.
    //
    // The 503 TWILIO_NOT_CONFIGURED response from GET indicates the
    // workspace has no connected Twilio integration — the UI should
    // render a "connect Twilio first" empty state.
    getVoiceConfig: builder.query<VoiceConfig, void>({
      query: () => ({ url: '/telephony/voice/config' }),
      providesTags: [{ type: 'VoiceConfig', id: 'CURRENT' }],
    }),
    updateVoiceConfig: builder.mutation<
      VoiceConfig,
      {
        recordingMode: 'off' | 'always';
        playRecordingDisclosure: boolean;
        voipRecordingMode?: 'manual' | 'automatic';
      }
    >({
      query: (body) => ({
        url: '/telephony/voice/config',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'VoiceConfig', id: 'CURRENT' }],
    }),

    // ============================================================
    // Incoming per-call recording (additive, per-Call-SID)
    // ============================================================
    // Two operations scoped to the ringing incoming call:
    //   1. broadcast the toggle change to the same user's other
    //      surface via realtime (web ↔ mobile sync);
    //   2. start recording after the accepted call is connected.
    // Both are idempotent by CallSid on the server side.
    postIncomingRecordingPreference: builder.mutation<
      { ok: true },
      { callSid: string; record: boolean; sourceDevice: 'web' | 'mobile'; timestamp: number }
    >({
      query: ({ callSid, ...body }) => ({
        url: `/telephony/incoming-calls/${encodeURIComponent(callSid)}/recording/preference`,
        method: 'POST',
        body,
      }),
    }),
    startIncomingCallRecording: builder.mutation<
      { ok: boolean; recordingStarted?: boolean; recordingSid?: string; idempotent?: boolean; error?: string; message?: string },
      { callSid: string; record: boolean }
    >({
      query: ({ callSid, record }) => ({
        url: `/telephony/incoming-calls/${encodeURIComponent(callSid)}/recording`,
        method: 'POST',
        body: { record },
      }),
    }),

    // ============================================================
    // Send Outbound SMS
    // ============================================================
    sendSms: builder.mutation<
      {
        success: boolean;
        messageId?: string;
        twilioSid?: string;
        from: string;
        to: string;
      },
      { toNumber: string; body: string; fromNumberId?: string; contactId?: string }
    >({
      query: (body) => ({
        url: '/telephony/sms/send',
        method: 'POST',
        body,
      }),
      // Invalidate Thread/Message lists since SMS persists into CommunicationThread
      invalidatesTags: [
        { type: 'Thread', id: 'LIST' },
        { type: 'Message', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListPhoneNumbersQuery,
  useGetPhoneNumberQuery,
  useRegisterPhoneNumberMutation,
  useUpdatePhoneNumberMutation,
  useAssignPhoneNumberMutation,
  useDeletePhoneNumberMutation,
  useSyncPhoneNumbersMutation,
  useListPhoneNumbersWithAssignmentsQuery,
  useSetPhoneNumberPrimaryMutation,
  useUpdatePhoneNumberLabelMutation,
  useGetDefaultOutboundNumberQuery,
  useListCallSessionsQuery,
  useGetCallSessionQuery,
  useStartCallRecordingMutation,
  useStopCallRecordingMutation,
  useListRecordingsQuery,
  useListVoicemailsQuery,
  useGetVoicemailQuery,
  useMarkVoicemailListenedMutation,
  useArchiveVoicemailMutation,
  useInitiateCallMutation,
  useSendSmsMutation,
  useFetchVoiceTokenMutation,
  useGetVoiceConfigQuery,
  useUpdateVoiceConfigMutation,
  usePostIncomingRecordingPreferenceMutation,
  useStartIncomingCallRecordingMutation,
  useGetRecordingModeQuery,
  useUpdateRecordingModeMutation,
  useGetRecordingEffectiveStateQuery,
} = telephonyApi;
